import _ from "lodash";
import PositionHelper from "./position_helper";

class AnnotationGenerator {
  static Annotation = class {
    type = null;
    text = null;
    position = null;
    altPosition = null;
    dimensions = null;
    object = null;
    allObjects = [];
    isAlt = false;

    constructor(data) {
      this.type = data.type;
      this.text = data.text;
      this.position = {
        x: Math.round(data.position.x),
        y: Math.round(data.position.y),
      };
      this.altPosition = {
        x: Math.round(data.altPosition.x),
        y: Math.round(data.altPosition.y),
      };
      this.dimensions = data.dimensions;
      this.object = data.object;
      this.allObjects = data.allObjects;
    }

    isHeight() {
      return this.type === "height";
    }

    isWidth() {
      return this.type === "width";
    }

    key() {
      return `${this.text}/${this.isHeight() ? "y" : "x"}/${
        this.isHeight() ? this.position.y : this.position.x
      }`;
    }
  };

  objects = [];
  size = 200;
  offsets = { module: 150, group: 150 + this.size, wall: 150 + 2 * this.size };
  annotations = [];
  cameraView = "front";
  groups = [];

  constructor(objects, options = {}) {
    this.objects = objects;
    this.cameraView = options.cameraView || "front";
    this.groups = options.groups || [];
  }

  findGroup(groupId) {
    return this.groups.find((group) => group.id === groupId);
  }

  #populate() {
    this.annotations = [];

    // Normalize rotation to 0-360 range
    const normalizeRotation = (rot) => {
      const numRot = Number(rot) || 0;
      return ((numRot % 360) + 360) % 360;
    };

    this.objects.forEach((object) => {
      const bounds = PositionHelper.getBoundsOfObject(object);

      // Get rotation, with fallback to group rotation for modules
      let rotation = normalizeRotation(object.rotation);
      let group = null;
      if (object.type === "module" && object.group_id) {
        group = this.findGroup(object.group_id);
        if (group && rotation === 0) {
          rotation = normalizeRotation(group.rotation);
        }
      }

      // Check rotation for both modules (with group fallback) and groups
      const effectiveRotation =
        object.type === "group" ? normalizeRotation(object.rotation) : rotation;

      // In top view, use z position for vertical axis instead of y
      // For modules in rotated groups, calculate vertical position (same logic as renderBorder)
      let verticalPosition;
      if (
        this.cameraView === "top" &&
        object.type === "module" &&
        object.group_id &&
        group
      ) {
        const groupRotation = normalizeRotation(group.rotation);
        const sideView = [90, 270].includes(groupRotation);

        if (sideView) {
          // For 90°/270° rotation: use group.position.z - (group.position.x - object.position.x)
          verticalPosition =
            group.position.z - (group.position.x - object.position.x);
        } else if (groupRotation === 180) {
          // For 180° rotation: use group.position.z + (groupMaxDepth - moduleMaxDepth)
          const groupMaxDepth = group.position.z + group.dimensions.depth;
          const moduleMaxDepth = object.position.z + object.dimensions.depth;
          verticalPosition =
            group.position.z + (groupMaxDepth - moduleMaxDepth);
        } else {
          verticalPosition = object.position.z || 0;
        }
      } else {
        verticalPosition =
          this.cameraView === "top" ? object.position.z || 0 : bounds.yMin;
      }

      const verticalMax =
        this.cameraView === "top"
          ? (object.position.z || 0) + (object.dimensions.depth || 0)
          : bounds.yMax;

      if (object.type !== "group") {
        // Height annotations
        // In top view, height annotation shows depth (vertical dimension from top)
        // In front view, height annotation shows height
        let heightText = object.dimensions.height;
        let heightDimension = object.dimensions.height;

        if (this.cameraView === "top") {
          // In top view, height annotation shows depth when not rotated
          // When rotated 90°/270°, width and depth swap, so show width
          if ([90, 270].includes(effectiveRotation)) {
            heightText = object.dimensions.width;
            heightDimension = object.dimensions.width;
          } else {
            heightText = object.dimensions.depth;
            heightDimension = object.dimensions.depth;
          }
        }

        // Calculate height annotation x position
        // For modules in rotated groups, adjust x position (same logic as renderBorder)
        let heightAnnotationX =
          bounds.xMin - this.size - this.offsets[object.type];
        if (
          this.cameraView === "top" &&
          object.type === "module" &&
          object.group_id &&
          group
        ) {
          const groupRotation = normalizeRotation(group.rotation);
          let adjustedX = bounds.xMin;

          if (groupRotation === 90) {
            const groupMaxDepth = group.position.z + group.dimensions.depth;
            const moduleMaxDepth = object.position.z + object.dimensions.depth;
            adjustedX = group.position.x + (groupMaxDepth - moduleMaxDepth);
          } else if (groupRotation === 180) {
            const groupMaxWidth = group.position.x + group.dimensions.width;
            const moduleMaxWidth = object.position.x + object.dimensions.width;
            adjustedX = group.position.x + (groupMaxWidth - moduleMaxWidth);
          } else if (groupRotation === 270) {
            adjustedX =
              group.position.x + (group.position.z - object.position.z);
          }

          if (adjustedX !== bounds.xMin) {
            heightAnnotationX =
              adjustedX - this.size - this.offsets[object.type];
          }
        }

        this.annotations.push(
          new AnnotationGenerator.Annotation({
            type: "height",
            text: heightText,
            position: {
              x: heightAnnotationX,
              y: verticalPosition,
            },
            altPosition: {
              x: bounds.xMax + this.offsets[object.type],
              y: verticalPosition,
            },
            dimensions: { height: heightDimension, width: this.size },
            object: object,
            allObjects: [object],
          })
        );
      }

      // For width annotations, handle rotation for both front and top view
      let widthText = object.dimensions.width;
      let widthDimension = object.dimensions.width;
      let widthPositionX = bounds.xMin;

      if (this.cameraView === "top") {
        // In top view, for rotated objects (90° or 270°), show depth instead of width
        if ([90, 270].includes(effectiveRotation)) {
          widthText = object.dimensions.depth;
          widthDimension = object.dimensions.depth;
        }
      }

      // Adjust position for modules in rotated groups (same logic as renderBorder)
      if (
        this.cameraView === "top" &&
        object.type === "module" &&
        object.group_id &&
        group
      ) {
        const groupRotation = normalizeRotation(group.rotation);

        if (groupRotation === 90) {
          const groupMaxDepth = group.position.z + group.dimensions.depth;
          const moduleMaxDepth = object.position.z + object.dimensions.depth;
          widthPositionX = group.position.x + (groupMaxDepth - moduleMaxDepth);
        } else if (groupRotation === 180) {
          const groupMaxWidth = group.position.x + group.dimensions.width;
          const moduleMaxWidth = object.position.x + object.dimensions.width;
          widthPositionX = group.position.x + (groupMaxWidth - moduleMaxWidth);
        } else if (groupRotation === 270) {
          widthPositionX =
            group.position.x + (group.position.z - object.position.z);
        }
      }

      // Calculate width annotation vertical position
      // In front view: use bottom positioning (subtract from yMin to place below)
      // In top view: use top positioning (add to bottom of object to place below)
      let widthAnnotationY;
      if (this.cameraView === "top") {
        // In top view, handle modules in rotated groups (same logic as renderBorder)
        if (object.type === "module" && object.group_id && group) {
          const groupRotation = normalizeRotation(group.rotation);
          const sideView = [90, 270].includes(groupRotation);

          if (sideView) {
            // For 90°/270° rotation: use group.position.z - (group.position.x - object.position.x)
            widthAnnotationY =
              group.position.z - (group.position.x - object.position.x);
            // Add the effective depth (width when rotated) and offset
            widthAnnotationY +=
              (object.dimensions.width || 0) + this.offsets[object.type] + 100;
          } else if (groupRotation === 180) {
            // For 180° rotation: use group.position.z + (groupMaxDepth - moduleMaxDepth)
            const groupMaxDepth = group.position.z + group.dimensions.depth;
            const moduleMaxDepth = object.position.z + object.dimensions.depth;
            widthAnnotationY =
              group.position.z + (groupMaxDepth - moduleMaxDepth);
            widthAnnotationY +=
              (object.dimensions.depth || 0) + this.offsets[object.type] + 100;
          } else {
            // No rotation: use standard calculation
            widthAnnotationY = verticalMax + this.offsets[object.type] + 100;
          }
        } else {
          // Standalone modules or groups
          // In top view, when rotated 90°/270°, width and depth swap
          // So the effective depth in z-axis is width, not depth
          if ([90, 270].includes(effectiveRotation)) {
            if (
              object.type === "group" &&
              object.modules &&
              object.modules.length > 0
            ) {
              // For groups, calculate based on modules' positions
              const moduleMaxZ = Math.max(
                ...object.modules.map(
                  (m) => (m.position.z || 0) + (m.dimensions.width || 0)
                )
              );
              widthAnnotationY = moduleMaxZ + this.offsets[object.type] + 100;
            } else {
              widthAnnotationY =
                (object.position.z || 0) +
                (object.dimensions.width || 0) +
                this.offsets[object.type] +
                100;
            }
          } else {
            if (
              object.type === "group" &&
              object.modules &&
              object.modules.length > 0
            ) {
              // For groups, calculate based on modules' positions
              const moduleMaxZ = Math.max(
                ...object.modules.map(
                  (m) => (m.position.z || 0) + (m.dimensions.depth || 0)
                )
              );
              widthAnnotationY = moduleMaxZ + this.offsets[object.type] + 100;
            } else {
              widthAnnotationY = verticalMax + this.offsets[object.type];
            }
          }
        }
      } else {
        widthAnnotationY = bounds.yMin - this.size - this.offsets[object.type] - 100;
      }

      this.annotations.push(
        new AnnotationGenerator.Annotation({
          type: "width",
          text: widthText,
          position: {
            x: widthPositionX,
            y: widthAnnotationY,
          },
          altPosition: {
            x: bounds.xMin,
            y:
              this.cameraView === "top"
                ? verticalPosition - this.offsets[object.type]
                : bounds.yMax + this.offsets[object.type],
          },
          dimensions: { height: this.size, width: widthDimension },
          object: object,
          allObjects: [object],
        })
      );
    });
  }

  #optimize() {
    const similarAnnotations = _.groupBy(this.annotations, (a) => a.key());

    this.annotations.forEach((annotation) => {
      const aBounds = PositionHelper.getBoundsOfObject(annotation);
      const isOverlapping = this.objects.some((object) => {
        return PositionHelper.isOverlapping(
          aBounds,
          PositionHelper.getBoundsOfObject(object)
        );
      });

      if (isOverlapping) {
        // Mark as conflicted. We will find an alternative for this later.
        annotation.conflict = true;

        // Check whether the alt position is clear.
        const altBounds = PositionHelper.getBounds(
          annotation.altPosition,
          annotation.dimensions
        );
        const isAltOverlapping = this.objects.some((object) => {
          return PositionHelper.isOverlapping(
            altBounds,
            PositionHelper.getBoundsOfObject(object)
          );
        });

        annotation.altValid = !isAltOverlapping;
      }
    });

    // Check for overlaps between annotations themselves
    this.annotations.forEach((annotation, index) => {
      if (annotation.conflict) return; // Skip already conflicted annotations

      const aBounds = PositionHelper.getBoundsOfObject(annotation);
      const overlappingAnnotation = this.annotations.find(
        (other, otherIndex) => {
          if (otherIndex === index) return false;
          if (other.conflict) return false;
          if (annotation.key() === other.key()) return false; // Same key will be merged later

          const otherBounds = PositionHelper.getBoundsOfObject(other);
          return PositionHelper.isOverlapping(aBounds, otherBounds);
        }
      );

      if (overlappingAnnotation) {
        // Mark as conflicted and check if alt position is available
        annotation.conflict = true;
        const altBounds = PositionHelper.getBounds(
          annotation.altPosition,
          annotation.dimensions
        );

        // Check if alt position overlaps with objects
        const isAltOverlappingObjects = this.objects.some((object) => {
          return PositionHelper.isOverlapping(
            altBounds,
            PositionHelper.getBoundsOfObject(object)
          );
        });

        // Check if alt position overlaps with non-conflicted annotations
        const isAltOverlappingAnnotations = this.annotations.some(
          (other, otherIndex) => {
            if (otherIndex === index) return false;
            if (other.conflict) return false; // Don't check conflicted ones as they might move
            const otherBounds = PositionHelper.getBoundsOfObject(other);
            return PositionHelper.isOverlapping(altBounds, otherBounds);
          }
        );

        annotation.altValid =
          !isAltOverlappingObjects && !isAltOverlappingAnnotations;
      }
    });

    this.annotations.forEach((annotation) => {
      if (annotation.conflict) {
        let otherAnnotations = similarAnnotations[annotation.key()];
        otherAnnotations = otherAnnotations.filter((a) => !a.conflict);
        let altAnnotation = null;

        if (otherAnnotations.length) {
          if (annotation.isHeight()) {
            // Take only annotations that are to the left of the current one.
            otherAnnotations = otherAnnotations.filter(
              (a) => a != annotation && a.position.x < annotation.position.x
            );
            // Sort from right-to-left so as to choose the right-most annotation.
            otherAnnotations = otherAnnotations.sort(
              (a, b) => a.position.x > b.position.x
            );
          } else {
            // Take only annotations that are to the bottom of the current one.
            otherAnnotations = otherAnnotations.filter(
              (a) => a != annotation && a.position.y < annotation.position.y
            );
            // Sort from top-to-bottom so as to choose the top-most annotation.
            otherAnnotations = otherAnnotations.sort(
              (a, b) => a.position.y > b.position.y
            );
          }

          altAnnotation = otherAnnotations[0];
        }

        if (altAnnotation) {
          altAnnotation.allObjects.push(annotation.object);
        } else {
          if (annotation.altValid) {
            // Alternate position is free. Use that as the position.
            annotation.position = annotation.altPosition;
            annotation.isAlt = true;
          } else {
            // No other position to render. Render at the conflicted point itself.
          }

          annotation.ignoreConflict = true;
        }
      }
    });
  }

  process() {
    this.#populate();
    this.#optimize();
  }

  getValidAnnotations() {
    return this.annotations.filter((a) => !a.conflict || a.ignoreConflict);
  }
}

export default AnnotationGenerator;
